import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js";
import Features from "../../../../Components/Features/ThreeColumnPages";
import {
  FaVideo,
  FaEye,
  FaRocket,
  FaMobileAlt,
  FaStar,
  FaCamera,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

export const HighQualityVideoFilmingInAlBarsha = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="High-Quality Video Filming in Al Barsha" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Elevate Your Vision</Subheading>}
        heading={
          <Heading>
            Professional{" "}
            <span tw="text-primary-500">High-Quality Video Filming</span> in Al
            Barsha
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, our high-quality video filming services in **Al
            Barsha** deliver cinematic, visually stunning footage for events,
            brands, and campaigns. Using state-of-the-art equipment, we capture
            every moment with precision and creativity to bring your story to
            life.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1601506521937-0b4218f2e801?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Film production setup
          "https://images.unsplash.com/photo-1540655037529-dec987208707?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Creative lighting effects
          "https://images.unsplash.com/photo-1580983218765-f663bec07b37?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Professional camera equipment
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Start Filming"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Premier Video Filming in Al Barsha"
        description="We deliver exceptional footage with professional expertise."
        cards={[
          {
            icon: <FaCamera color="#ff5464" size={40} />,
            title: "Cinematic Excellence",
            description: "High-resolution footage with stunning visuals.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Creative Storytelling",
            description: "Capture narratives that engage and inspire.",
          },
          {
            icon: <FaRocket color="#ff5464" size={40} />,
            title: "Efficient Production",
            description: "Timely filming to meet your deadlines.",
          },
          {
            icon: <FaMobileAlt color="#ff5464" size={40} />,
            title: "Versatile Output",
            description: "Content optimized for all platforms and devices.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Trusted Professionals",
            description: "Al Barsha’s go-to for high-quality filming.",
          },
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Advanced Equipment",
            description: "Top-tier cameras and tools for flawless results.",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our High-Quality Video Filming Process"
        description={
          <Description>
            We produce exceptional videos through a meticulous, creative
            process:
            <ul tw="mt-4 list-disc pl-6">
              <li>Consultation: Align with your vision and project goals.</li>
              <li>
                Planning: Develop a detailed filming schedule and shot list.
              </li>
              <li>
                Filming: Capture footage with cutting-edge cameras and lighting.
              </li>
              <li>
                Post-Production: Enhance with professional editing and effects.
              </li>
              <li>Review: Ensure the footage meets your expectations.</li>
              <li>
                Delivery: Provide polished videos ready for your platforms.
              </li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Team collaboration
          "https://images.unsplash.com/photo-1610374792793-f016b77ca51a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Studio production setup
          "https://images.unsplash.com/photo-1536240478700-b869070f9279?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Artistic video composition
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Video Filming Wins in Al Barsha"
        description="Our high-quality videos have elevated brands and events in Al Barsha."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Brand Campaign",
            description: "Filmed a viral ad that boosted brand visibility.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Event Highlights",
            description: "Captured a corporate event with cinematic flair.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Client Acclaim",
            description: "97% satisfaction from filming clients.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready for Stunning Footage?</Subheading>}
        heading="High-Quality Video Filming in Al Barsha"
        description={
          <Description>
            Partner with MilaajBrandset for professional high-quality video
            filming in Al Barsha. Create cinematic, engaging content that
            captivates your audience—contact us today!
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1536240478700-b869070f9279?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Video editing workspace
          "https://images.unsplash.com/photo-1546412414-e1885259563a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Dubai skyline
          "https://images.unsplash.com/photo-1523207911345-32501502db22?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80", // Professional camera equipment
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Now"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
